import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';

export interface FavoritesInterface {
  favoritesStore: {
    stops: Array<any> | undefined;
    lines: Array<any> | undefined;
    pois: Array<any> | undefined;
    events: Array<any> | undefined;
    plans: Array<any> | undefined;
    directions: Array<any> | undefined;
    agencyId: Array<any> | undefined;
    poiCategories: Array<any> | undefined;
    category: Array<any> | undefined;
  };
  menuItemFavorite: number | string | undefined;
}

// Define the initial state using that type
const initialState: any = {
  favoritesStore: {
    stops: [],
    lines: [],
    pois: [],
    events: [],
    plans: [],
    directions: [],
    agencyId: [],
    poiCategories: [],
  },
  menuItemFavorite: 0,
  category: [],
};

export const favoritesSlice = createSlice({
  name: 'favoritesSlice',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    updateFavorites: (state, action) => {
      state.favoritesStore = action.payload;
    },
    updateMenuFavorites: (state, action) => {
      state.menuItemFavorite = action.payload;
    },
    /* UPDATES */
    updateFavoritesPlans: (state, action) => {
      state.favoritesStore.plans = action.payload;
    },
    updateFavoritesLines: (state, action) => {
      state.favoritesStore.lines = action.payload;
    },
    updateFavoritesStops: (state, action) => {
      state.favoritesStore.stops = action.payload;
    },
    updateFavoritesDirections: (state, action) => {
      state.favoritesStore.directions = action.payload;
    },
    /* ADDS */
    addLinesFavorites: (state, action) => {
      state.favoritesStore.lines.push(action.payload);
    },
    addStopsFavorites: (state, action) => {
      state.favoritesStore.stops.push(action.payload);
    },
    addFavoritePois: (state, action) => {
      state.favoritesStore.pois.push(action.payload);
    },
    addFavoriteEvents: (state, action) => {
      state.favoritesStore.events.push(action.payload);
    },
    addFavoriteDirections: (state, action) => {
      state.favoritesStore.directions.push(action.payload);
    },
    addFavoritePlans: (state, action) => {
      state.favoritesStore.plans.push(action.payload);
    },
    /* DELETES */
    deleteLinesFavorites: (state, action) => {
      let myFavoritesLinesCopy = state.favoritesStore.lines;
      let lineIndexInFavorites = myFavoritesLinesCopy.findIndex(
        (line) => String(line.routeId) === String(action.payload),
      );
      if (lineIndexInFavorites !== -1) {
        myFavoritesLinesCopy.splice(lineIndexInFavorites, 1);
        state.favoritesStore.lines = myFavoritesLinesCopy;
      }
    },
    deleteStopsFavorites: (state, action) => {
      let myFavoritesStopsCopy = state.favoritesStore.stops;
      let stopIndexInFavorites = myFavoritesStopsCopy.findIndex(
        (stop) => String(stop.stopId) === String(action.payload),
      );
      if (stopIndexInFavorites !== -1) {
        myFavoritesStopsCopy.splice(stopIndexInFavorites, 1);
        state.favoritesStore.stops = myFavoritesStopsCopy;
      }
    },
    deletePoisFavorites: (state, action) => {
      let myFavoritesPoisCopy = state.favoritesStore.pois;
      let poiIndexInFavorites = myFavoritesPoisCopy.findIndex(
        (poi) => String(poi.poiId) === String(action.payload),
      );
      if (poiIndexInFavorites !== -1) {
        myFavoritesPoisCopy.splice(poiIndexInFavorites, 1);
        state.favoritesStore.pois = myFavoritesPoisCopy;
      }
    },
    deleteEventsFavorites: (state, action) => {
      let myFavoritesEventsCopy = state.favoritesStore.events;
      let eventIndexInFavorites = myFavoritesEventsCopy.findIndex(
        (event) => String(event.eventId) === String(action.payload),
      );
      if (eventIndexInFavorites !== -1) {
        myFavoritesEventsCopy.splice(eventIndexInFavorites, 1);
        state.favoritesStore.events = myFavoritesEventsCopy;
      }
    },
    deleteDirectionsFavorites: (state, action) => {
      let myFavoritesDirectionsCopy = state.favoritesStore.directions;
      let directionsIndexInFavorites = myFavoritesDirectionsCopy.findIndex(
        (dir: any) => String(dir.id) === String(action.payload),
      );
      if (directionsIndexInFavorites !== -1) {
        myFavoritesDirectionsCopy.splice(directionsIndexInFavorites, 1);
        state.favoritesStore.directions = myFavoritesDirectionsCopy;
      }
    },
    deleteFavoritePlans: (state, action) => {
      let myFavoritePlansCopy = state.favoritesStore.plans;
      let planIndexInFavorites = myFavoritePlansCopy.findIndex(
        (plan) => String(plan.id) === String(action.payload),
      );
      if (planIndexInFavorites !== -1) {
        myFavoritePlansCopy.splice(planIndexInFavorites, 1);
        state.favoritesStore.plans = myFavoritePlansCopy;
      }
    },
    updateCategoryFavorites: (state, action) => {
      state.category = action.payload;
    },
  },
});

export const {
  updateFavorites,
  deleteStopsFavorites,
  addStopsFavorites,
  addFavoritePois,
  addFavoriteEvents,
  addFavoriteDirections,
  addFavoritePlans,
  deleteFavoritePlans,
  deleteDirectionsFavorites,
  deletePoisFavorites,
  deleteEventsFavorites,
  addLinesFavorites,
  deleteLinesFavorites,
  updateFavoritesPlans,
  updateFavoritesLines,
  updateFavoritesStops,
  updateCategoryFavorites,
  updateMenuFavorites,
  updateFavoritesDirections,
} = favoritesSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const favoritesState = (state: RootState) => state.favorites.favoritesStore;
export const stopsFavoritesState = (state: RootState) => state.favorites.favoritesStore?.stops;
export const directionsFavoritesState = (state: RootState) =>
  state.favorites.favoritesStore?.directions;
export const poisFavoritesState = (state: RootState) => state.favorites.favoritesStore?.pois;
export const eventsFavoritesState = (state: RootState) => state.favorites.favoritesStore?.events;
export const plansFavoriteState = (state: RootState) => state.favorites.favoritesStore?.plans;
export const linesFavoriteState = (state: RootState) => state.favorites.favoritesStore?.lines;
export const categoriaFavoriteState = (state: RootState) => state.favorites?.category;
export const menuFavoritesState = (state: RootState) => state.favorites.menuItemFavorite;

export default favoritesSlice.reducer;
